import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { catalogApi } from "@/lib/apiClient";
import { formatMoney } from "@/lib/format";
import { HOME } from "@/constants/testIds";
import StoreHeader from "@/components/StoreHeader";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const { user } = useCustomerAuth();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [homepageWa, setHomepageWa] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      catalogApi.products({ featured: true, page_size: 4, sort: "featured" }).catch(() => ({ items: [] })),
      catalogApi.categories().catch(() => []),
      axios.get(`${BACKEND}/api/catalog/contact`).then((r) => r.data).catch(() => null),
    ]).then(([prods, cats, contact]) => {
      if (!alive) return;
      setFeatured(prods.items || []);
      setCategories(cats);
      setHomepageWa(contact?.homepage_whatsapp || "");
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <StoreHeader />

      {/* Hero — dark, brand-forward */}
      <section className="relative bg-neutral-950 text-white overflow-hidden border-b" data-testid="home-hero">
        {/* subtle red glow accents */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-red-600/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-red-700/10 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-28 flex flex-col items-center text-center">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50 mb-8">
            Sharjah &middot; UAE &middot; Genuine parts
          </div>

          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_uae-ecom-platform/artifacts/ncff0pq4_WhatsApp%20Image%202026-08-17%20at%205.24.37%20PM.jpeg"
            alt="Fast Force Auto Spare Parts"
            className="w-full max-w-2xl mx-auto rounded-xl shadow-2xl ring-1 ring-white/10"
            data-testid="home-hero-brand"
          />

          <p className="mt-10 max-w-xl text-base sm:text-lg text-white/70">
            OEM and premium aftermarket parts for American vehicles.
            <span className="block mt-1 text-white/50">Trade-priced. Warranty-backed. Delivered fast.</span>
          </p>

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Button asChild size="lg" className="bg-red-600 hover:bg-red-500 text-white border-0 transition-transform hover:scale-105" data-testid="home-cta-catalog">
              <Link to="/catalog">Browse catalogue</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white transition-transform hover:scale-105"
              data-testid="home-cta-whatsapp"
            >
              <a href={`https://wa.me/${(homepageWa || "971527129647").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer">
                WhatsApp us
              </a>
            </Button>
          </div>

          {!user && (
            <div className="mt-8 text-sm text-white/60" data-testid="home-cta-signin-wrap">
              Returning customer?{" "}
              <Link
                to="/customer/login"
                className="text-white underline underline-offset-4 hover:text-red-400"
                data-testid="home-cta-signin"
              >
                Sign in
              </Link>{" "}
              for faster checkout.
            </div>
          )}
        </div>
      </section>

      {/* Why Fast Force */}
      <section className="border-b bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
            Why Fast Force
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <Feature title="Genuine + OEM parts" body="Sourced through vetted channels for American vehicles. Every part warranty-backed." />
            <Feature title="UAE-wide delivery" body="Low delivery charges with transparent per-emirate fees shown before you check out." />
            <Feature title="Customer support" body="WhatsApp us on +971 52 712 9647 for VIN lookup, quotes and fitting help." />
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="border-b">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
              Shop by category
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/catalog?category=${c.slug}`}
                  className="border rounded-lg px-4 py-6 text-center hover:bg-red-50 hover:border-red-300 hover:scale-105 transition-all duration-200"
                >
                  <div className="font-medium text-sm">{c.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="border-b">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Featured</div>
                <h2 className="text-2xl font-semibold mt-1">This week&rsquo;s picks</h2>
              </div>
              <Link to="/catalog" className="text-sm text-muted-foreground hover:text-foreground">
                View all &rarr;
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  className="group border rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
                >
                  <div className="aspect-square bg-muted overflow-hidden">
                    {p.main_image && (
                      <img src={p.main_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    {p.brand && (
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        {p.brand}
                      </div>
                    )}
                    <div className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                      {p.name}
                    </div>
                    <div className="font-semibold">{formatMoney(p.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Staff / Admin sign-in */}
      <section className="border-b bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Staff area</div>
            <div className="text-lg font-medium">Admin &amp; team members</div>
            <div className="text-sm text-slate-300 mt-1">
              Access the dashboard to manage orders, inventory and invoices.
            </div>
          </div>
          <Button asChild size="lg" variant="secondary" data-testid="home-cta-admin-login">
            <Link to="/admin/login">Admin sign in &rarr;</Link>
          </Button>
        </div>
      </section>

      <footer className="py-10 text-center text-xs text-muted-foreground">
        <div>FAST FORCE Auto Spare Parts Trading L.L.C · Sharjah, UAE · TRN 100563539400003</div>
        <div className="mt-3 space-x-4">
          <Link to="/track" data-testid="home-cta-track" className="hover:text-foreground">
            Track order
          </Link>
          <Link to="/admin/login" data-testid={HOME.ctaAdmin} className="hover:text-foreground">
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({ title, body }) {
  return (
    <div className="rounded-lg bg-white border p-5">
      <div className="font-medium">{title}</div>
      <div className="text-sm text-muted-foreground mt-2">{body}</div>
    </div>
  );
}
